import { getBlockedByCategoryUrl } from '../chrome/routing';
import { redirectToURL } from '../actions/navigationActions';

/**
 * The value of the blocked source URL is added as a parameter to the blocker page URL and encoded in Base64.
 *
 * @param blockerPageUrl - The blocker page URL to parse
 * @param sourceBlockedUrl - The blocked source URL to encode
 * @returns The final parsed blockerPageUrl
 */
export const parseBlockerPageUrl = (
  blockerPageUrl: string,
  sourceBlockedUrl: string
): string => {
  const encodedUrl = btoa(sourceBlockedUrl);
  // The blockerPageUrl may have a hash fragment, so we need to add the query parameter before the hash
  const baseUrl = blockerPageUrl.split('#')[0];
  const hashFragment = blockerPageUrl.split('#')[1];
  const hashPart = hashFragment ? `#${hashFragment}` : '';
  return `${baseUrl}?u=${encodedUrl}${hashPart}`;
};

/**
 * Checks if a URL is a blocker page URL.
 *
 * @param url - The URL to check
 * @returns True if the URL is a blocker page, false otherwise
 */
export const isBlockerPage = (url: string): boolean => {
  return url.includes('chrome-extension://') && url.includes('blocker.html');
};

/**
 * Extracts the original URL from a blocker page URL by decoding the 'u' parameter.
 *
 * @param url - The blocker page URL containing the encoded original URL
 * @returns The decoded original URL, or null if extraction fails
 */
export const extractOriginalUrlFromBlockerPage = (
  url: string
): string | null => {
  try {
    const urlObj = new URL(url);
    const encodedUrl = urlObj.searchParams.get('u');

    if (encodedUrl) {
      const decodedUrl = atob(encodedUrl);
      return decodedUrl;
    }

    return null;
  } catch (error) {
    console.warn('Failed to extract original URL from blocker page:', error);
    return null;
  }
};

/**
 * Redirects a tab to the blocked by category screen
 *
 * @param tabId - The ID of the tab to redirect
 * @param blockedUrl - Optional URL that was blocked
 * @returns Promise that resolves when redirect is complete
 */
export const redirectToBlockedByCategoryScreen = (
  tabId: number,
  blockedUrl?: string
): Promise<chrome.tabs.Tab | void> => {
  const blockerUrl = getBlockedByCategoryUrl();
  const finalUrl = blockedUrl
    ? parseBlockerPageUrl(blockerUrl, blockedUrl)
    : blockerUrl;
  return redirectToURL(tabId, finalUrl);
};
